import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import type { SowingInstruction } from "../../services/sowingInstructionsApi";
import { useRevokeSowingInstruction } from "../../hooks/useSowingInstructions";
import { useNotification } from "@/shared/hooks/useNotification";

interface RevokeSowingDialogProps {
  instruction: SowingInstruction;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const RevokeSowingDialog = ({
  instruction,
  open,
  onOpenChange,
  onSuccess,
}: RevokeSowingDialogProps) => {
  const [reason, setReason] = useState("");
  const revokeMutation = useRevokeSowingInstruction();
  const notify = useNotification();

  const handleRevoke = () => {
    if (!reason.trim()) {
      notify.error("Please provide a reason for revocation");
      return;
    }

    revokeMutation.mutate(
      {
        sowingInstructionId: instruction.id,
        reason,
      },
      {
        onSuccess: () => {
          setReason("");
          onSuccess();
        },
        onError: (error: any) => {
          notify.error(error.response?.data?.message || "Failed to revoke instruction");
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revoke Sowing Instruction</DialogTitle>
          <DialogDescription>
            {instruction.variety} - {instruction.quantityToSow} units
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg bg-red-50 border border-red-200 p-3">
            <p className="text-sm text-red-800">
              This action cannot be undone. The instruction will be marked as revoked.
            </p>
          </div>

          <Field>
            <FieldLabel>
              Reason for Revocation <span className="text-red-500">*</span>
            </FieldLabel>
            <Textarea
              placeholder="Explain why this instruction is being revoked..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={revokeMutation.isPending}
            />
          </Field>

          <div className="flex gap-2 justify-end">
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={revokeMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRevoke}
              disabled={revokeMutation.isPending || !reason.trim()}
            >
              {revokeMutation.isPending ? "Revoking..." : "Revoke"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};